import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { FixContentView } from '../components/FixPanel.js';
import { LogoMark, landingUrl } from '../components/Layouts.js';
import { ThemeToggle } from '../components/ThemeToggle.js';
import { Card, Loading } from '../components/ui.js';
import { formatDate } from '../i18n.js';

/**
 * Ready-made fixes the site owner sent to their web designer (T-72; fix.html): no sign-in, valid
 * for 30 days, in the owner's language (so no language switch). Each fix with the values to copy
 * and whether it is done — as the site's checks found, not a box to tick: nothing is stored here.
 */
export function SharedFixes() {
  const { token = '' } = useParams();
  const { i18n } = useTranslation();
  const page = useQuery({
    queryKey: ['public-fixes', token],
    queryFn: () => unwrap(api.GET('/public/fixes/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const t = i18n.getFixedT(page.data?.locale ?? i18n.language);
  const brand = t('app.name');
  const site = landingUrl();

  let content: ReactNode;
  if (page.isPending) content = <Loading />;
  else if (page.isError) {
    const expired = page.error instanceof ApiError && page.error.code === 'TOKEN_EXPIRED';
    content = (
      <div className="empty">
        <b>{expired ? t('sharedFixes.expiredTitle') : t('sharedFixes.notFoundTitle')}</b>
        <span>{expired ? t('sharedFixes.expired') : t('sharedFixes.notFound')}</span>
      </div>
    );
  } else {
    const d = page.data;
    content =
      d.items.length === 0 ? (
        <div className="empty">
          <b>{t('sharedFixes.title', { site: d.siteName })}</b>
          <span>{t('sharedFixes.empty')}</span>
        </div>
      ) : (
        <>
          <div>
            <h1>{t('sharedFixes.title', { site: d.siteName })}</h1>
            <p className="lead">
              {t('sharedFixes.intro', { account: d.accountName, host: d.host })}
            </p>
            <p className="valid">
              {t('sharedFixes.valid', { date: formatDate(d.expiresAt, d.locale) })}
            </p>
          </div>
          {d.message ? (
            <Card className="message">
              <b className="h4s">{t('sharedFixes.message', { account: d.accountName })}</b>
              <p className="bodyM">{d.message}</p>
            </Card>
          ) : null}
          {d.items.map((item) => (
            <article key={item.ruleCode} className="card fixitem">
              <h2>
                <span>{item.title}</span>
                {item.done ? <span className="badge ok nodot">{t('sharedFixes.done')}</span> : null}
              </h2>
              <FixContentView fix={item.content} t={t} />
            </article>
          ))}
          <footer>
            <span>{t('sharedFixes.noSecrets')}</span>
            <span aria-hidden="true">·</span>
            <span>{t('sharedFixes.by', { brand })}</span>
          </footer>
        </>
      );
  }

  return (
    // The owner's language for the page's text, so that it is read out in that language.
    <main className="fixpage" lang={page.data?.locale}>
      <div className="top">
        {site ? (
          <a className="logo" href={site} aria-label={brand}>
            <LogoMark />
            {brand}
          </a>
        ) : (
          <span className="logo">
            <LogoMark />
            {brand}
          </span>
        )}
        <ThemeToggle />
      </div>
      {content}
    </main>
  );
}
